import {createElement, ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, ClickAwayListener, Paper, Popper, Skeleton, Typography, useTheme} from "@mui/material";
import {Add} from "@mui/icons-material";
import {getDownloadURL, ref as storageRef} from "firebase/storage";
import {storage} from "../Util/firebaseConfig";
import {Keyword, RichTextNode} from "../Util/types";

type RichTextProps = {
    content: RichTextNode[],
    // Keyword definitions available to this render (body + all popups).
    keywords?: Keyword[],
    // Which terms to tag in the top-level body. Defaults to every keyword.
    scope?: string[],
    // Colour used for tagged keywords. Defaults to the theme primary.
    color?: string
}

export type RenderContext = {
    keywordMap: Record<string, Keyword>,
    used: Set<string>,
    scope: string[],
    color: string
}

// Builds a fresh RenderContext for a top-level render pass (article body or
// popup description) from a flat list of keyword definitions.
export function createKeywordContext(keywords: Keyword[], color: string, scope?: string[]): RenderContext {
    const keywordMap: Record<string, Keyword> = {};
    for (const entry of keywords) {
        keywordMap[entry.keyword.toLowerCase()] = entry;
    }
    return {
        keywordMap,
        used: new Set<string>(),
        scope: scope ?? keywords.map((entry) => entry.keyword),
        color,
    };
}

const ALLOWED_TAGS = new Set([
    "p", "h1", "h2", "h3", "h4", "h5", "h6",
    "strong", "b", "em", "i", "u", "s", "small", "mark",
    "blockquote", "ul", "ol", "li", "a", "span", "div",
    "code", "pre", "sub", "sup", "figure", "figcaption", "br", "hr",
]);
const VOID_TAGS = new Set(["br", "hr"]);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const isDirectSrc = (src: string) =>
    /^(https?:)?\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:");

// Resolves an image node's source. If it is already a URL we use it directly,
// otherwise we treat it as a Firebase Storage path and fetch the download URL.
export function ArticleImage({src}: {src: string}) {
    const [url, setUrl] = useState<string | undefined>(() => (isDirectSrc(src) ? src : undefined));
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (isDirectSrc(src)) {
            setUrl(src);
            return;
        }
        let active = true;
        getDownloadURL(storageRef(storage, src))
            .then((resolved) => {
                if (active) {
                    setUrl(resolved);
                }
            })
            .catch(() => {
                if (active) {
                    setFailed(true);
                }
            });
        return () => {
            active = false;
        };
    }, [src]);

    if (failed) {
        return null;
    }
    if (!url) {
        return <Skeleton variant="rectangular" height={200} sx={{borderRadius: 1, my: 2}}/>;
    }
    return (
        <Box
            component="img"
            src={url}
            loading="lazy"
            sx={{display: "block", maxWidth: "60%", height: "auto", pt: 4, pb: 4, margin: "auto", borderRadius: 1, my: 2}}
        />
    );
}

// A collapsible section rendered as a grey accordion bar (the Learn-page
// "dropdown"). The title sits on the bar; the body expands beneath it and can
// contain further nodes, including nested dropdowns.
export function DropdownSection({title, color, children}: {title: string, color: string, children: ReactNode}) {
    return (
        <Accordion
            disableGutters
            square
            elevation={0}
            sx={{"my": 1, "&:before": {display: "none"}}}
        >
            <AccordionSummary
                expandIcon={<Add/>}
                sx={{
                    "backgroundColor": "#e9e9e9",
                    "borderLeft": `4px solid ${color}`,
                    "&:hover": {backgroundColor: "#dedede"},
                    "& .MuiAccordionSummary-content": {fontWeight: 700},
                }}
            >
                {title}
            </AccordionSummary>
            <AccordionDetails sx={{pt: 2}}>{children}</AccordionDetails>
        </Accordion>
    );
}

type KeywordTermProps = {
    term: string,
    definition: Keyword,
    color: string,
    keywordMap: Record<string, Keyword>
}

// A bold, coloured, hoverable term. Hovering (or tapping) reveals a popup with
// the keyword's description, which is itself rendered rich text and can contain
// its own nested keywords.
function KeywordTerm({term, definition, color, keywordMap}: KeywordTermProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
        }
    };
    const openFrom = (element: HTMLElement) => {
        cancelClose();
        setAnchorEl(element);
        setOpen(true);
    };
    const scheduleClose = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 120);
    };

    useEffect(() => () => cancelClose(), []);

    const descriptionContext: RenderContext = {
        keywordMap,
        used: new Set<string>(),
        scope: definition.keywords ?? [],
        color,
    };

    return (
        <>
            <Box
                component="span"
                onMouseEnter={(event) => openFrom(event.currentTarget)}
                onMouseLeave={scheduleClose}
                onClick={(event) => (open ? setOpen(false) : openFrom(event.currentTarget))}
                sx={{color, fontWeight: 700, cursor: "pointer", borderBottom: `1px dotted ${color}`}}
            >
                {term}
            </Box>
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="top"
                style={{zIndex: 1400}}
                modifiers={[{name: "offset", options: {offset: [0, 8]}}]}
            >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper
                        elevation={6}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                        sx={{p: 1.5, maxWidth: 320, borderTop: `3px solid ${color}`}}
                    >
                        <Typography sx={{fontWeight: 700, color, mb: 0.5}}>{definition.keyword}</Typography>
                        <Box sx={{fontSize: "0.9rem"}}>
                            {renderNodes(definition.description ?? [], descriptionContext)}
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

// Walks a text string and tags the first, not-yet-used occurrence of each
// in-scope keyword as a KeywordTerm. `used` is shared across the whole pass so
// each keyword is only tagged once, in document order.
export function renderText(text: string, ctx: RenderContext): ReactNode[] {
    const {scope, used, keywordMap, color} = ctx;
    const nodes: ReactNode[] = [];
    let remaining = text;
    let counter = 0;

    while (remaining.length > 0) {
        let match: {index: number, text: string, key: string} | null = null;

        for (const term of scope) {
            const key = term.toLowerCase();
            if (used.has(key) || !keywordMap[key]) {
                continue;
            }
            const found = new RegExp(`\\b${escapeRegExp(term)}\\b`, "i").exec(remaining);
            if (!found) {
                continue;
            }
            if (match === null || found.index < match.index ||
                (found.index === match.index && found[0].length > match.text.length)) {
                match = {index: found.index, text: found[0], key};
            }
        }

        if (match === null) {
            nodes.push(remaining);
            break;
        }

        if (match.index > 0) {
            nodes.push(remaining.slice(0, match.index));
        }
        used.add(match.key);
        nodes.push(
            <KeywordTerm
                key={`kw-${counter}`}
                term={match.text}
                definition={keywordMap[match.key]}
                color={color}
                keywordMap={keywordMap}
            />,
        );
        remaining = remaining.slice(match.index + match.text.length);
        counter += 1;
    }

    return nodes;
}

function renderNode(node: RichTextNode, ctx: RenderContext, key: number): ReactNode {
    if (node.type === "img") {
        return <ArticleImage key={key} src={typeof node.content === "string" ? node.content : ""}/>;
    }

    if (node.type === "dropdown") {
        const body = typeof node.content === "string" ?
            renderText(node.content, ctx) :
            renderNodes(node.content, ctx);
        return (
            <DropdownSection key={key} title={node.title ?? ""} color={ctx.color}>
                {body}
            </DropdownSection>
        );
    }

    const tag = ALLOWED_TAGS.has(node.type) ? node.type : "span";
    if (VOID_TAGS.has(tag)) {
        return createElement(tag, {key});
    }

    const children = typeof node.content === "string" ?
        renderText(node.content, ctx) :
        renderNodes(node.content, ctx);
    return createElement(tag, {key}, children);
}

function renderNodes(nodes: RichTextNode[], ctx: RenderContext): ReactNode[] {
    return nodes.map((node, index) => renderNode(node, ctx, index));
}

// Renders an array of rich-text nodes with inline keyword tagging.
export default function RichText({content, keywords = [], scope, color}: RichTextProps) {
    const theme = useTheme();
    const resolvedColor = color ?? theme.palette.primary.main;

    const context = useMemo(
        () => createKeywordContext(keywords, resolvedColor, scope),
        [keywords, resolvedColor, scope],
    );

    return <>{renderNodes(content, context)}</>;
}
